/**
 * Conversation Facade (100% Shared)
 * 
 * Wraps all conversation-related Redux actions.
 * This facade is completely platform-agnostic and can be used
 * by both web and plugin without any modifications.
 * 
 * Usage:
 * ```tsx
 * const conversation = useConversationFacade();
 * await conversation.create({ init_message: 'Hello', mode: 'design' });
 * ```
 * 
 * @module useConversationFacade
 * @category Facades
 */

import { useCallback } from 'react';
import { useAppDispatch } from '../../../store/hooks';
import {
  createNewConversation,
  resetConversation,
  setConversationId,
  setMode,
  setName,
  setLlmFamily,
  setOnboardingPrototyper,
  setInitiatedFromUncover
} from '../../../store/slices/conversationSlice';
import type { ConversationStepMode } from '../../../types/conversation.types';

export interface ConversationFacade {
  /**
   * Create a new conversation
   * @param params - Conversation creation parameters
   * @returns void - Dispatches Redux action synchronously
   */
  create: (params: {
    init_message: string;
    mode: ConversationStepMode;
    skip_analysis?: boolean;
    attachmentIds?: string[];
    llmFamily?: string;
  }) => void;

  /**
   * Reset conversation state to initial values
   */
  reset: () => void;

  /**
   * Set current conversation ID
   * @param id - Conversation ID
   */
  setId: (id: string) => void;

  /**
   * Set conversation mode
   * @param mode - Conversation step mode
   */
  setMode: (mode: ConversationStepMode) => void;

  /**
   * Set conversation name
   * @param name - Conversation name
   */
  setName: (name: string) => void;

  /**
   * Set LLM model family
   * @param llmFamily - LLM model family (DeepSeek, Grok, Gemini)
   */
  setLlmFamily: (llmFamily: string) => void;

  /**
   * Set onboarding prototyper flag (accessed from onboarding)
   * @param value - Boolean flag
   */
  setOnboardingPrototyper: (value: boolean) => void;

  /**
   * Set initiated-from-uncover flag
   * @param value - Boolean flag
   */
  setInitiatedFromUncover: (value: boolean) => void;
}

/**
 * Conversation Facade Hook
 * 
 * Provides conversation management capabilities.
 * 100% shared between web and plugin.
 */
export function useConversationFacade(): ConversationFacade {
  const dispatch = useAppDispatch();

  return {
    create: useCallback(
      (params) => dispatch(createNewConversation(params)),
      [dispatch]
    ),

    reset: useCallback(
      () => dispatch(resetConversation()),
      [dispatch]
    ),

    setId: useCallback(
      (id: string) => dispatch(setConversationId(id)),
      [dispatch]
    ),

    setMode: useCallback(
      (mode: ConversationStepMode) => dispatch(setMode(mode)),
      [dispatch]
    ),

    setName: useCallback(
      (name: string) => dispatch(setName(name)),
      [dispatch]
    ),

    setLlmFamily: useCallback(
      (llmFamily: string) => dispatch(setLlmFamily(llmFamily)),
      [dispatch]
    ),

    setOnboardingPrototyper: useCallback(
      (value: boolean) => dispatch(setOnboardingPrototyper(value)),
      [dispatch]
    ),

    setInitiatedFromUncover: useCallback(
      (value: boolean) => dispatch(setInitiatedFromUncover(value)),
      [dispatch]
    ),
  };
}
